import { createFileRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { ProjectPage } from "../components/projects/ProjectPage";
import { ProjectHeading } from "../components/projects/ProjectHeading";
import { IssuePeek } from "../components/issues/IssuePeek";
import { useDeleteWithUndo } from "../components/issues/use-delete-with-undo";
import { CalendarView } from "../components/calendar/CalendarView";
import { rangeOf, stepOf, type CalendarMode, type CalendarPeriod } from "../components/calendar/layout";
import { dayLabel } from "../components/reports/charts";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Unavailable } from "../components/ui/states";
import { useCalendar } from "../lib/calendar";
import { useMoveDates } from "../lib/timeline";
import { useProject } from "../lib/projects";
import { workspaceSlug } from "../lib/hosts";
import { personTimezone } from "../lib/timezone";
import { cn } from "../lib/cn";
import { strings } from "../lib/strings";
import { fill } from "../lib/words";
import { calendarDateSchema } from "../../shared/schemas/issues";
import { dayAt, weekStartOf } from "../../shared/zoned";
import type { PublicProject } from "../../shared/types/projects";

const words = strings.calendar;
const PERIODS = ["month", "week"] as const;
const MODES = ["due", "span"] as const;
const MONTH = new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric", timeZone: "UTC" });

/** The calendar's address: a month or a week, due dates or spans, the day it is on, and the issue open in the peek. */
const calendarSearchSchema = z.object({
  period: z.enum(PERIODS).optional().catch(undefined),
  mode: z.enum(MODES).optional().catch(undefined),
  at: calendarDateSchema.optional().catch(undefined),
  issue: z.string().max(40).optional().catch(undefined),
});

/** A row of buttons, one pressed: the period, or what the calendar shows. */
function Choice<T extends string>({
  label,
  options,
  names,
  value,
  onChoose,
}: {
  label: string;
  options: readonly T[];
  names: Record<T, string>;
  value: T;
  onChoose: (value: T) => void;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex w-fit rounded-md border border-[hsl(var(--border))] p-0.5"
    >
      {options.map((each) => (
        <button
          key={each}
          type="button"
          aria-pressed={value === each}
          onClick={() => onChoose(each)}
          className={cn(
            "rounded px-2.5 py-1 text-sm focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:outline-none",
            value === each
              ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"
              : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--accent))]",
          )}
        >
          {names[each]}
        </button>
      ))}
    </div>
  );
}

function Calendar({ project, canManage }: { project: PublicProject; canManage: boolean }) {
  const search = useSearch({ from: "/admin/p/$projectKey/calendar" });
  const navigate = useNavigate({ from: "/admin/p/$projectKey/calendar" });
  const slug = workspaceSlug();
  const period: CalendarPeriod = search.period ?? "month";
  const mode: CalendarMode = search.mode ?? "due";
  // Opened on today where the person is; the project's own today is marked when its answer comes.
  const anchor = search.at ?? dayAt(Date.now(), personTimezone());
  const calendar = useCalendar(project.id, rangeOf(anchor, period));
  const move = useMoveDates(project.id);
  // Issues move for whoever may change the project's issues.
  const canEdit = useProject(project.id).data?.canEdit === true;
  const deleteWithUndo = useDeleteWithUndo(project.id);
  const answer = calendar.data;
  const peeked = search.issue === undefined ? undefined : answer?.issues.find((issue) => issue.key === search.issue);
  const go = (at: string | undefined) => void navigate({ search: (prev) => ({ ...prev, at }), replace: true });
  const name =
    period === "month"
      ? MONTH.format(new Date(`${anchor.slice(0, 7)}-01T00:00:00Z`))
      : fill(words.week, { day: dayLabel(weekStartOf(anchor, answer?.weekStart ?? 1)) });
  return (
    <div className="mx-auto max-w-7xl space-y-6 px-6 py-8">
      <header className="flex flex-col gap-4">
        <ProjectHeading project={project} projectKey={project.key} current="calendar" canManage={canManage} />
        <p className="text-sm text-[hsl(var(--muted-foreground))]">{words.body}</p>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              aria-label={words.previous[period]}
              onClick={() => go(stepOf(anchor, period, -1))}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </Button>
            <Button variant="outline" size="sm" onClick={() => go(undefined)}>
              {words.goToday}
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label={words.next[period]}
              onClick={() => go(stepOf(anchor, period, 1))}
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>
          <h2 className="min-w-40 text-lg font-semibold" aria-live="polite">
            {name}
          </h2>
          <div className="ml-auto flex flex-wrap gap-3">
            <Choice
              label={words.periodLabel}
              options={PERIODS}
              names={words.periods}
              value={period}
              onChoose={(each) =>
                void navigate({
                  search: (prev) => ({ ...prev, period: each === "month" ? undefined : each }),
                  replace: true,
                })
              }
            />
            <Choice
              label={words.modeLabel}
              options={MODES}
              names={words.modes}
              value={mode}
              onChoose={(each) =>
                void navigate({
                  search: (prev) => ({ ...prev, mode: each === "due" ? undefined : each }),
                  replace: true,
                })
              }
            />
          </div>
        </div>
      </header>
      {answer === undefined && calendar.isError ? (
        <Unavailable what={words.theCalendar} onRetry={() => void calendar.refetch()} />
      ) : answer === undefined ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <div className={cn("space-y-3 transition-opacity", calendar.isPlaceholderData && "opacity-60")}>
          {answer.more && <p className="text-sm text-[hsl(var(--muted-foreground))]">{words.tooMany}</p>}
          <CalendarView
            answer={answer}
            period={period}
            mode={mode}
            anchor={anchor}
            canEdit={canEdit}
            onOpen={(issue) => void navigate({ search: (prev) => ({ ...prev, issue: issue.key }) })}
            onChange={(change) =>
              move.mutateAsync([change]).then(
                (refused) => {
                  if (refused.length > 0) {
                    toast.error(fill(words.refused, { key: change.key }));
                    return false;
                  }
                  const from = change.startDate ?? change.dueDate!;
                  const to = change.dueDate ?? change.startDate!;
                  toast.success(
                    mode === "due"
                      ? fill(words.moved, { key: change.key, day: dayLabel(change.dueDate!) })
                      : from === to
                        ? fill(words.moved, { key: change.key, day: dayLabel(from) })
                        : fill(words.movedSpan, { key: change.key, from: dayLabel(from), to: dayLabel(to) }),
                  );
                  return true;
                },
                () => {
                  toast.error(fill(words.failed, { key: change.key }));
                  return false;
                },
              )
            }
          />
          {answer.issues.length === 0 && !calendar.isPlaceholderData && (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{words.empty}</p>
          )}
        </div>
      )}
      {peeked !== undefined && (
        <IssuePeek
          issueId={peeked.id}
          issueKey={peeked.key}
          slug={slug}
          projectId={project.id}
          pageNeeds={720}
          onClose={() => void navigate({ search: (prev) => ({ ...prev, issue: undefined }) })}
          onExpand={() =>
            void navigate({
              to: "/admin/p/$projectKey/$issueKey",
              params: { projectKey: project.key, issueKey: peeked.key },
            })
          }
          onRequestDelete={() =>
            deleteWithUndo.requestDelete(peeked.id, {
              onStart: () => void navigate({ search: (prev) => ({ ...prev, issue: undefined }) }),
            })
          }
        />
      )}
    </div>
  );
}

/** A project's calendar (BUILD_SPEC §12.6). */
function Page() {
  const { projectKey } = useParams({ from: "/admin/p/$projectKey/calendar" });
  return (
    <ProjectPage projectKey={projectKey}>
      {(project, canManage) => <Calendar project={project} canManage={canManage} />}
    </ProjectPage>
  );
}

export const Route = createFileRoute("/admin/p/$projectKey/calendar")({
  validateSearch: calendarSearchSchema,
  component: Page,
});
